import type { Body, Meta, UppyFile } from '@uppy/core'
import { Uppy } from '@uppy/core'
import AwsS3Multipart from '@uppy/aws-s3'
import { Subject } from 'rxjs'
import {
  abortMultipartUpload,
  completeMultipartUpload,
  listMultipartParts,
  registerPoolFile,
  signMultipartPart,
  startMultipartUpload,
  verifyPoolFile
} from './repository/PoolUploadRepository.ts'

export type WizardUploadMeta = Meta & {
  draftId: string
  fileKey: string
  orderId: string
  poolId: string
  pendingId?: string
}

type WizardUppyFile = UppyFile<WizardUploadMeta, Body>

/** What went wrong, for telemetry: an HTTP status comes with `http`. */
export type UploadFailure = 'http' | 'network' | 'aborted' | 'unconfirmed' | 'already_held' | 'other'

export type UploadEvent =
  | { type: 'started'; draftId: string; key: string }
  | { type: 'progress'; draftId: string; key: string; progress: number }
  /** `retries`: automatic re-uploads it took. */
  | { type: 'succeeded'; draftId: string; key: string; serverFileId: string; retries?: number }
  | { type: 'failed'; draftId: string; key: string; error: string; failure?: UploadFailure; httpStatus?: number; retries?: number }
  /** A transient failure, with another attempt on its way; `attempt` counts from 1. */
  | { type: 'retrying'; draftId: string; key: string; message: string; failure?: UploadFailure; httpStatus?: number; attempt?: number }

export type UploadTarget = { orderId: string; poolId: string }

/** Progress below this step is not worth a store update; large uploads report many times a second. */
const PROGRESS_STEP = 0.02

class AlreadyHeldError extends Error {}

/* the pool got the name outside this draft; a reload reconciles the draft with the pool's files by name */
const alreadyHeld = (fileName: string) =>
  new AlreadyHeldError(
    `The pool already holds a file named ${fileName} that this page does not know. If it is this file, reload the page to use it; if it is a different file, rename yours and drop it again.`
  )

/* the S3 plugin types its callbacks with Uppy's base meta, like the classic page's `hasUploadMeta` guard handles */
const wizardFile = (file: UppyFile<Meta, Body>): WizardUppyFile => {
  const meta = file.meta
  if (
    typeof meta.draftId !== 'string' ||
    typeof meta.fileKey !== 'string' ||
    typeof meta.orderId !== 'string' ||
    typeof meta.poolId !== 'string'
  ) {
    throw new Error('Missing upload metadata')
  }
  return file as WizardUppyFile
}

const pendingIdOf = (file: UppyFile<Meta, Body>): string | undefined =>
  typeof file.meta.pendingId === 'string' ? file.meta.pendingId : undefined

const errorText = (error: unknown): string => (error instanceof Error ? error.message : String(error ?? 'upload failed'))

export const RETRYING_TEXT = 'Connection interrupted — retrying…'
export const INTERRUPTED_TEXT = 'Upload interrupted — retry or drop the file again'

/* pauses before each automatic re-upload of a transient failure; the last failure is reported */
export const UPLOAD_RETRY_DELAYS_MS = [2000, 10_000, 30_000] as const

type UploadError = {
  isNetworkError?: boolean
  name?: string
  message?: string
  code?: string
  status?: number
  source?: { status?: number }
  response?: { status?: number }
}

/**
 * A failure worth uploading again unasked: the browser aborted the request (leaving or backgrounding the
 * page does), the connection dropped (status 0, or no response at all), or the server erred (5xx).
 * Never a 4xx, nor an error of our own making such as a name the pool already holds.
 */
export const isTransientUploadError = (error: unknown, response?: { status?: number }): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false
  }
  const e = error as UploadError
  if (e.name === 'AbortError') {
    return true
  }
  const status = statusOf(e, response)
  if (typeof status === 'number') {
    return status === 0 || status >= 500
  }
  return e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED' || e.message === 'Network Error'
}

const statusOf = (e: UploadError, response?: { status?: number }) => response?.status ?? e.source?.status ?? e.status ?? e.response?.status

/** The failure as a category and HTTP status, never its message: messages can carry file names. */
export const uploadFailureOf = (error: unknown, response?: { status?: number }): { failure: UploadFailure; httpStatus?: number } => {
  if (error instanceof AlreadyHeldError) {
    return { failure: 'already_held' }
  }
  if (typeof error !== 'object' || error === null) {
    return response?.status ? { failure: 'http', httpStatus: response.status } : { failure: 'other' }
  }
  const e = error as UploadError
  if (e.name === 'AbortError') {
    return { failure: 'aborted' }
  }
  const status = statusOf(e, response)
  if (typeof status === 'number' && status > 0) {
    return { failure: 'http', httpStatus: status }
  }
  if (status === 0 || e.isNetworkError || e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED' || e.message === 'Network Error') {
    return { failure: 'network' }
  }
  return { failure: 'other' }
}

/* S3 can take a while to list a large multipart object: the last pauses are long rather than giving up early */
export const VERIFY_DELAYS_MS = [1000, 3000, 10_000, 30_000] as const

type Verification = { draftId: string; fileKey: string; orderId: string; poolId: string; pendingId: string }

/**
 * Asks the server to confirm an uploaded file, again after a pause when it cannot yet. Only a confirmed
 * file is listed as completed and may be referenced by an item.
 */
export const confirmUpload = async (
  { orderId, poolId, pendingId }: Verification,
  verify: typeof verifyPoolFile = verifyPoolFile,
  delaysMs: readonly number[] = VERIFY_DELAYS_MS
): Promise<{ ok: true } | { ok: false; error: string }> => {
  let reason = 'the server could not find the uploaded file'
  for (let attempt = 0; attempt <= delaysMs.length; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, delaysMs[attempt - 1]))
    }
    try {
      const confirmed = await verify(orderId, poolId, pendingId)
      if (confirmed?.verifiedAt) {
        return { ok: true }
      }
      reason = 'the server could not find the uploaded file'
    } catch (error) {
      reason = errorText(error)
    }
  }
  return { ok: false, error: `Uploaded, but the server did not confirm the file (${reason})` }
}

type UnloadTarget = Pick<Window, 'addEventListener' | 'removeEventListener'>

/* the browser's own "leave site?" prompt; returnValue for browsers that ignore preventDefault here */
const holdUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault()
  event.returnValue = ''
}

/**
 * Uploads for wizard drafts, independent of any page and of the classic `UppyProvider`: one Uppy instance
 * per draft, since Uppy de-duplicates by name and size, which must not collide across drafts.
 */
export class WizardUploadManager {
  readonly events$ = new Subject<UploadEvent>()
  private readonly uppys = new Map<string, Uppy<WizardUploadMeta, Body>>()
  private readonly fileIds = new Map<string, string>()
  private readonly reported = new Map<string, number>()
  private readonly unverified = new Map<string, Verification>()
  private readonly autoRetries = new Map<string, number>()
  private readonly retryTimers = new Map<string, ReturnType<typeof setTimeout>>()

  /* uploads go on behind every page of the tab, so a reload or close anywhere would lose them */
  private readonly inFlight = new Set<string>()

  constructor(
    private readonly verifyDelaysMs: readonly number[] = VERIFY_DELAYS_MS,
    private readonly retryDelaysMs: readonly number[] = UPLOAD_RETRY_DELAYS_MS,
    private readonly unloadTarget: UnloadTarget | undefined = typeof window === 'undefined' ? undefined : window
  ) {
    this.events$.subscribe((event) => {
      const id = this.id(event.draftId, event.key)
      if (event.type === 'succeeded' || event.type === 'failed') {
        this.settle([id])
      } else {
        this.track(id)
      }
    })
  }

  private track(id: string) {
    if (this.inFlight.size === 0) {
      this.unloadTarget?.addEventListener('beforeunload', holdUnload)
    }
    this.inFlight.add(id)
  }

  private settle(ids: readonly string[]) {
    const before = this.inFlight.size
    ids.forEach((id) => this.inFlight.delete(id))
    if (before > 0 && this.inFlight.size === 0) {
      this.unloadTarget?.removeEventListener('beforeunload', holdUnload)
    }
  }

  private id(draftId: string, key: string) {
    return `${draftId}\u0000${key}`
  }

  private uppyFor(draftId: string): Uppy<WizardUploadMeta, Body> {
    const existing = this.uppys.get(draftId)
    if (existing) {
      return existing
    }
    const uppy = new Uppy<WizardUploadMeta, Body>({ id: `order-wizard-${draftId}`, autoProceed: true })
    uppy.use(AwsS3Multipart, {
      getUploadParameters: async (file) => {
        const target = wizardFile(file)
        const upload = await registerPoolFile(target.meta.orderId, target.meta.poolId, target.name ?? '', 'SAMPLE')
        if (!upload) {
          throw alreadyHeld(target.name ?? 'The file')
        }
        uppy.setFileMeta(target.id, { ...target.meta, pendingId: upload.id })
        return { method: 'PUT', url: upload.location }
      },
      createMultipartUpload: async (file) => {
        const target = wizardFile(file)
        const upload = await registerPoolFile(target.meta.orderId, target.meta.poolId, target.name ?? '', 'SAMPLE')
        if (!upload) {
          throw alreadyHeld(target.name ?? 'The file')
        }
        const started = await startMultipartUpload(upload.id, target.type)
        uppy.setFileMeta(target.id, { ...target.meta, pendingId: upload.id })
        return { uploadId: started.uploadId, key: started.key }
      },
      listParts: (file, { uploadId, key }) => listMultipartParts(uploadId ?? '', key, pendingIdOf(file)),
      abortMultipartUpload: (file, { uploadId, key }) => abortMultipartUpload(uploadId ?? '', key, pendingIdOf(file)),
      completeMultipartUpload: (file, { uploadId, key, parts }) => completeMultipartUpload(pendingIdOf(file), uploadId, key, parts),
      signPart: (file, { uploadId, partNumber }) => signMultipartPart(uploadId, partNumber, pendingIdOf(file))
    })
    uppy.on('upload-progress', (file, progress) => this.onProgress(file, progress.bytesUploaded, progress.bytesTotal))
    uppy.on('upload-success', (file) => void this.onSuccess(uppy, file))
    uppy.on('upload-error', (file, error, response) => this.onError(uppy, file, error, response))
    this.uppys.set(draftId, uppy)
    return uppy
  }

  private onError(uppy: Uppy<WizardUploadMeta, Body>, file: WizardUppyFile | undefined, error: unknown, response?: { status?: number }) {
    if (!file) {
      return
    }
    const { draftId, fileKey } = file.meta
    const id = this.id(draftId, fileKey)
    const attempt = this.autoRetries.get(id) ?? 0
    const transient = isTransientUploadError(error, response)
    const cause = uploadFailureOf(error, response)
    if (transient && attempt < this.retryDelaysMs.length) {
      this.autoRetries.set(id, attempt + 1)
      this.events$.next({ type: 'retrying', draftId, key: fileKey, message: RETRYING_TEXT, ...cause, attempt: attempt + 1 })
      this.retryTimers.set(
        id,
        setTimeout(() => {
          this.retryTimers.delete(id)
          /* the draft may have let go of the file, or of its uploader, in the meantime */
          if (this.uppys.get(draftId) === uppy && this.fileIds.get(id) === file.id && uppy.getFile(file.id)) {
            this.reported.delete(id)
            void uppy.retryUpload(file.id)
          }
        }, this.retryDelaysMs[attempt])
      )
      return
    }
    this.autoRetries.delete(id)
    this.events$.next({ type: 'failed', draftId, key: fileKey, error: transient ? INTERRUPTED_TEXT : errorText(error), ...cause, retries: attempt })
  }

  private forgetRetries(id: string) {
    clearTimeout(this.retryTimers.get(id))
    this.retryTimers.delete(id)
    this.autoRetries.delete(id)
  }

  private onProgress(file: WizardUppyFile | undefined, uploaded: number, total: number | null) {
    if (!file || !total) {
      return
    }
    const id = this.id(file.meta.draftId, file.meta.fileKey)
    const progress = Math.min(uploaded / total, 1)
    if (progress - (this.reported.get(id) ?? 0) < PROGRESS_STEP) {
      return
    }
    this.reported.set(id, progress)
    this.events$.next({ type: 'progress', draftId: file.meta.draftId, key: file.meta.fileKey, progress })
  }

  private async onSuccess(uppy: Uppy<WizardUploadMeta, Body>, file: WizardUppyFile | undefined) {
    if (!file) {
      return
    }
    const { draftId, fileKey, orderId, poolId } = file.meta
    const retries = this.autoRetries.get(this.id(draftId, fileKey)) ?? 0
    this.forgetRetries(this.id(draftId, fileKey))
    const pendingId = uppy.getFile(file.id)?.meta.pendingId ?? file.meta.pendingId
    if (!pendingId) {
      this.events$.next({
        type: 'failed',
        draftId,
        key: fileKey,
        error: 'The server did not return an id for the uploaded file',
        failure: 'other',
        retries
      })
      return
    }
    await this.verify({ draftId, fileKey, orderId, poolId, pendingId }, retries)
  }

  /* single-PUT uploads are otherwise never marked verified, which hides them from the completed-files list */
  private async verify(target: Verification, retries = 0) {
    const id = this.id(target.draftId, target.fileKey)
    const outcome = await confirmUpload(target, verifyPoolFile, this.verifyDelaysMs)
    if (outcome.ok) {
      this.unverified.delete(id)
      this.events$.next({ type: 'succeeded', draftId: target.draftId, key: target.fileKey, serverFileId: target.pendingId, retries })
    } else {
      this.unverified.set(id, target)
      this.events$.next({ type: 'failed', draftId: target.draftId, key: target.fileKey, error: outcome.error, failure: 'unconfirmed', retries })
    }
  }

  enqueue(draftId: string, target: UploadTarget, files: { key: string; file: File }[]) {
    const uppy = this.uppyFor(draftId)
    for (const { key, file } of files) {
      const id = this.id(draftId, key)
      const previous = this.fileIds.get(id)
      const live = previous ? uppy.getFile(previous) : undefined
      if (live && !live.error) {
        continue
      }
      if (live) {
        uppy.removeFile(live.id)
      }
      try {
        const fileId = uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
          meta: { draftId, fileKey: key, orderId: target.orderId, poolId: target.poolId }
        })
        this.fileIds.set(id, fileId)
        this.reported.delete(id)
        this.forgetRetries(id)
        this.events$.next({ type: 'started', draftId, key })
      } catch (error) {
        this.events$.next({ type: 'failed', draftId, key, error: errorText(error), failure: 'other', retries: 0 })
      }
    }
  }

  /**
   * Uploads a failed file again, or only asks again for the confirmation of one that arrived. `false` when
   * the file is not in the uploader any more (after a reload), so it has to be dropped again.
   */
  retry(draftId: string, key: string): boolean {
    const unverified = this.unverified.get(this.id(draftId, key))
    if (unverified) {
      this.events$.next({ type: 'started', draftId, key })
      void this.verify(unverified)
      return true
    }
    const uppy = this.uppys.get(draftId)
    const fileId = this.fileIds.get(this.id(draftId, key))
    if (!uppy || !fileId || !uppy.getFile(fileId)) {
      return false
    }
    this.reported.delete(this.id(draftId, key))
    this.forgetRetries(this.id(draftId, key))
    this.track(this.id(draftId, key))
    void uppy.retryUpload(fileId)
    return true
  }

  /** Cancels and forgets every upload of the draft whose file is not in `keys`. */
  retain(draftId: string, keys: ReadonlySet<string>) {
    const uppy = this.uppys.get(draftId)
    const prefix = `${draftId}\u0000`
    const dropped = (id: string) => id.startsWith(prefix) && !keys.has(id.slice(prefix.length))
    for (const [id, fileId] of [...this.fileIds]) {
      if (dropped(id)) {
        if (uppy?.getFile(fileId)) {
          uppy.removeFile(fileId)
        }
        this.fileIds.delete(id)
        this.reported.delete(id)
        this.forgetRetries(id)
      }
    }
    for (const id of [...this.unverified.keys()]) {
      if (dropped(id)) {
        this.unverified.delete(id)
      }
    }
    this.settle([...this.inFlight].filter(dropped))
  }

  discard(draftId: string) {
    const uppy = this.uppys.get(draftId)
    if (uppy) {
      uppy.cancelAll()
      uppy.destroy()
      this.uppys.delete(draftId)
    }
    for (const id of [...this.fileIds.keys()]) {
      if (id.startsWith(`${draftId}\u0000`)) {
        this.fileIds.delete(id)
        this.reported.delete(id)
        this.forgetRetries(id)
      }
    }
    for (const id of [...this.unverified.keys()]) {
      if (id.startsWith(`${draftId}\u0000`)) {
        this.unverified.delete(id)
      }
    }
    this.settle([...this.inFlight].filter((id) => id.startsWith(`${draftId}\u0000`)))
  }
}

export const wizardUploads = new WizardUploadManager()
